import React, { useState, useEffect, useMemo } from "react";
import timesheetApi from "../../../store/services/timesheet.service";
import CustomButton from "../../../components/controls/button/Button";
import { ArrowLeft, ArrowRight, CalendarIcon, NotFoundIcon } from "../../../modules/default-svg";
import styles from "../timesheet.module.scss";
import moment from "moment";
import $ from "jquery";
import NotFoundLayout from "../../../components/controls/NotFound/NotFound";
import { Tooltip } from "@fluentui/react-components";
import { Lock } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const WeeklyTimesheetGrid = ({
  year,
  week,
  isPM,
  userId = null,
  projectId = null,
  currentWeek: currentWeekProp,
  onWeekChange,
  showWeekend,
  setShowWeekend,
  onSaveSuccess
}) => {
  const [timesheetData, setTimesheetData] = useState([]);
  const [originalData, setOriginalData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState({});
  const [submitLoading, setSubmitLoading] = useState({});
  const [projectChanges, setProjectChanges] = useState({});

  const currentWeek = currentWeekProp;

  const getFormattedDateRange = () => {
    if (!currentWeek) return '';

    const startDate = moment(currentWeek.startDate);
    const endDate = showWeekend
      ? moment(currentWeek.endDate)
      : moment(currentWeek.startDate).add(4, 'days');

    return `${startDate.format("DD MMM YYYY")} – ${endDate.format("DD MMM YYYY")}`;
  };

  useEffect(() => {
    if (year && week) {
      fetchTimesheetData();
    }
  }, [year, week, userId, projectId]);

  useEffect(() => {
    detectChanges();
  }, [editedData, originalData]);

  useEffect(() => {
    if (!currentWeek) return;

    const handleDateChange = (start) => {
      const weekStart = start.clone().startOf('isoWeek');
      const weekEnd = weekStart.clone().endOf('isoWeek');

      const newWeek = {
        year: weekEnd.year(),
        week: weekStart.isoWeek(),
        startDate: weekStart.toDate(),
        endDate: weekEnd.toDate(),
      };
      onWeekChange(newWeek);
    };

    $("input[name='weekRangePicker']").daterangepicker(
      {
        startDate: moment(currentWeek.startDate),
        endDate: moment(currentWeek.endDate),
        singleDatePicker: true,
        autoApply: true,
        maxDate: moment(),
        locale: {
          format: 'DD MMM YYYY'
        }
      },
      handleDateChange
    );

    return () => {
      if ($("input[name='weekRangePicker']").data('daterangepicker')) {
        $("input[name='weekRangePicker']").daterangepicker("destroy");
      }
    };
  }, [currentWeek, showWeekend, onWeekChange]);

  const fetchTimesheetData = async () => {
    setLoading(true);
    try {
      const response = userId && projectId
        ? await timesheetApi.getUserWeekForValidation({ userId, projectId, year, week })
        : await timesheetApi.getMyWeek({ year, week });

      const dataArray = Array.isArray(response) ? response : [response];
      setTimesheetData(dataArray);
      setOriginalData(JSON.parse(JSON.stringify(dataArray)));
      initializeEditedData(dataArray);
    } catch (error) {
      console.error("Error fetching timesheet data:", error);
    } finally {
      setLoading(false);
    }
  };

  const initializeEditedData = (dataArray) => {
    const edited = {};
    dataArray.forEach((projectData) => {
      edited[projectData.project.id] = {};
      DAYS.forEach((day, index) => {
        const dayData = projectData?.entries?.[index] || {};
        edited[projectData.project.id][day] = {
          hours: dayData.hours || 0,
        };
      });
    });
    setEditedData(edited);
  };

  const detectChanges = () => {
    if (!originalData || !originalData.length) {
      setProjectChanges({});
      return;
    }

    const changes = {};
    originalData.forEach((projectData) => {
      const projectId = projectData.project.id;
      const hasProjectChanges = DAYS.some((day, index) => {
        const original = projectData?.entries?.[index] || {};
        const edited = editedData[projectId]?.[day] || {};
        return original.hours !== edited.hours;
      });
      changes[projectId] = hasProjectChanges;
    });

    setProjectChanges(changes);
  };

  const handleHoursChange = (projectId, day, value) => {
    const hours = parseFloat(value) || 0;
    setEditedData((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        [day]: { ...prev[projectId]?.[day], hours },
      }
    }));
  };

  const handleApplyToRemaining = (projectId, sourceDayIndex) => {
    const sourceDay = DAYS[sourceDayIndex];
    const hours = editedData[projectId]?.[sourceDay]?.hours || 0;
    const endDay = showWeekend ? 6 : 4;
    const newEditedData = { ...editedData };

    for (let i = sourceDayIndex + 1; i <= endDay; i++) {
      if (!newEditedData[projectId]) newEditedData[projectId] = {};
      newEditedData[projectId][DAYS[i]] = { hours };
    }
    setEditedData(newEditedData);
  };

  const handleSave = async (projectData) => {

    if (!isTimesheetEntryAllowed(projectData)) return;
    const projectId = projectData.project.id;
    setSaveLoading(prev => ({ ...prev, [projectId]: true }));
    try {
      const payload = {
        projectId: projectData.project.id,
        year,
        week,
        dailyBreakdown: DAYS.map((day, index) => ({
          dayOfWeek: index,
          hours: editedData[projectData.project.id]?.[day]?.hours || 0,
          notes: "",
        })),
      };

      if (projectData?.id) {
        payload.id = projectData.id;
      }

      if (userId) {
        await timesheetApi.saveUserTimesheet({ ...payload, userId });
      } else {
        await timesheetApi.saveMyTimesheet(payload);
      }

      await fetchTimesheetData();

      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error("Error saving timesheet:", error);
    } finally {
      setSaveLoading(prev => ({ ...prev, [projectId]: false }));
    }
  };

  const handleSubmit = async (projectData) => {
    if (!projectData?.id) return;
    if (!isTimesheetEntryAllowed(projectData)) return;

    const projectId = projectData.project.id;
    setSubmitLoading(prev => ({ ...prev, [projectId]: true }));
    try {
      if (userId) {
        await timesheetApi.submitUserTimesheet(projectData.id);
      } else {
        await timesheetApi.submitMyTimesheet(projectData.id);
      }
      await fetchTimesheetData();
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error("Error submitting timesheet:", error);
    } finally {
      setSubmitLoading(prev => ({ ...prev, [projectId]: false }));
    }
  };

  const isTimesheetEntryAllowed = (projectData) => {
    return projectData?.isTimesheetEntryAllowed;
  }

  const isEditable = (projectData) => {
    if (!projectData) return false;
    if (!isTimesheetEntryAllowed(projectData)) return false;
    const status = projectData.statusSummary;
    const isFutureWeek = moment().endOf('isoWeek').year() < year ||
      (moment().endOf('isoWeek').year() === year && moment().endOf('isoWeek').week() < week);

    if (isFutureWeek) return false;

    if (isPM) return true;

    return status === 0 || status === 1;
  };

  const canSubmit = (projectData) => {
    if (!projectData) return false;
    if (!isTimesheetEntryAllowed(projectData)) return false;

    const projectId = projectData.project.id;
    return !projectChanges[projectId] && projectData?.statusSummary === 0 && projectData?.id;
  };

  const getTotalHours = (projectId) => {
    return DAYS.reduce((sum, day) => sum + (editedData[projectId]?.[day]?.hours || 0), 0);
  };

  const getOverallTotalHours = () => {
    return timesheetData.reduce((sum, projectData) =>
      sum + getTotalHours(projectData.project.id), 0
    );
  };

  const getOverallMaxHours = () => {
    return timesheetData.reduce((sum, p) => sum + (p.maxWeeklyHours || 0), 0);
  };

  const getStatusLabel = (status) => {
    const labels = { 0: "Draft", 1: "Submitted", 2: "Validated" };
    return labels[status] || "-";
  };

  const displayDays = showWeekend ? DAYS : DAYS.slice(0, 5);
  const displayDaysShort = showWeekend ? DAYS_SHORT : DAYS_SHORT.slice(0, 5);

  if (!year || !week) {
    return (
      <div className={styles.emptyState}>
        <p>Please select a week to view timesheet</p>
      </div>
    );
  }

  return (
    <div className={styles.weeklyGrid}>
      {loading ? (
        <div className={styles.skeletonWrapper}>
          <div className={styles.skeletonHeader}>
            <div className={styles.skeletonBadge}></div>
            <div className={styles.skeletonTotal}></div>
            <div className={styles.skeletonToggle}></div>
          </div>
          <div className={styles.skeletonGrid}>
            <div className={styles.skeletonRow}>
              <div className={styles.skeletonCell}></div>
              {[...Array(showWeekend ? 7 : 5)].map((_, i) => (
                <div key={i} className={styles.skeletonCell}></div>
              ))}
            </div>
            <div className={styles.skeletonRow}>
              <div className={styles.skeletonCell}></div>
              {[...Array(showWeekend ? 7 : 5)].map((_, i) => (
                <div key={i} className={styles.skeletonCell}>
                  <div className={styles.skeletonInput}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.gridHeader}>
            <div className={styles.headerLeft}>
              {(() => {
                const total = Number(getOverallTotalHours().toFixed(2));
                const max = Number(getOverallMaxHours().toFixed(2));
                const isOver = total > max;
                const label = `${total} / ${max}h`;
                return (
                  <div className={styles.totalHours}>
                    Total:{" "}
                    {isOver ? (
                      <Tooltip
                        content={`You have exceeded the maximum weekly hours (${max}h)`}
                        relationship="label"
                      >
                        <strong className={styles.hoursOver}>{label}</strong>
                      </Tooltip>
                    ) : (
                      <strong>{label}</strong>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className={styles.headerRight}>
              <label className={styles.weekendToggle}>
                <input
                  type="checkbox"
                  checked={showWeekend}
                  onChange={(e) => setShowWeekend(e.target.checked)}
                />
                <span>Show Weekend</span>
              </label>
            </div>
          </div>

          {timesheetData.length === 0 ? (
            <div className={styles.emptyState}>
              <NotFoundLayout
                icon={<NotFoundIcon />}
                heading="No records found"
              />
            </div>
          ) : (
            <div className={styles.compactTimesheetGrid}>
              {timesheetData.map((projectData) => {
                const projectId = projectData.project.id;
                const hasChanges = projectChanges[projectId] || false;
                const projectTotal = Number(getTotalHours(projectId).toFixed(2));
                const projectMax = Number((projectData.maxWeeklyHours || 0).toFixed(2));
                const isProjectOver = projectTotal > projectMax;
                const projectHoursLabel = `${projectTotal} / ${projectMax}h`;

                return (
                  <div key={projectId} className={styles.compactProjectRow}>
                    <div className={styles.compactProjectInfo}>
                      <div className="flex align-items-center gap-2">
                        <span className={styles.projectPill}>{projectData.project.name}</span>
                        {isProjectOver ? (
                          <Tooltip
                            content={`Exceeded max weekly hours (${projectMax}h) for this project`}
                            relationship="label"
                          >
                            <span className={`${styles.compactTotal} ${styles.hoursOver}`}>
                              {projectHoursLabel}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className={styles.compactTotal}>{projectHoursLabel}</span>
                        )}
                      </div>
                      <div className={styles.compactProjectMeta}>
                        {!isTimesheetEntryAllowed(projectData) && (
                              <Tooltip
                                content={`Timesheet entry locked for this project`}
                                relationship="label"
                              >
                                <Lock size={16} color="#342c82" strokeWidth={1}/>
                              </Tooltip>
                            )}
                        <div className={styles.statusBadge}>
                          <span className={styles[`status${projectData.statusSummary || 0}`]}>
                            {getStatusLabel(projectData.statusSummary)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className={styles.compactDaysGrid}>
                      {displayDays.map((day, index) => {
                        const dayIndex = DAYS.indexOf(day);
                        const canApply = dayIndex < (showWeekend ? 6 : 4);
                        const dayDate = currentWeek
                          ? moment(currentWeek.startDate).add(dayIndex, 'days').format('DD')
                          : '';

                        return (
                          <div key={day} className={styles.compactDayCell}>
                            <div className={styles.compactDayHeader}>
                              <span className={styles.compactDayName}>{displayDaysShort[index]}</span>
                              <span className={styles.compactDayDate}>{dayDate}</span>
                            </div>
                            <div className={styles.compactInputWrapper}>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                max="24"
                                value={editedData[projectId]?.[day]?.hours || 0}
                                onChange={(e) => handleHoursChange(projectId, day, e.target.value)}
                                disabled={!isEditable(projectData)}
                                className={styles.compactHoursInput}
                              />
                              {isEditable(projectData) && canApply && (
                                <button
                                  type="button"
                                  className={styles.compactCopyButton}
                                  onClick={() => handleApplyToRemaining(projectId, dayIndex)}
                                  title="Apply to next days"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M11 14h10" />
                                    <path d="M16 4h2a2 2 0 0 1 2 2v1.344" />
                                    <path d="m17 18 4-4-4-4" />
                                    <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 1.793-1.113" />
                                    <rect x="8" y="2" width="8" height="4" rx="1" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className={styles.compactActions}>
                      {hasChanges && isEditable(projectData) && (
                        <CustomButton
                          label="Save"
                          clickHandler={() => handleSave(projectData)}
                          btnClass={styles.saveButton}
                          showLoading={saveLoading[projectId]}
                          isDisabled={loading || saveLoading[projectId]}
                        />
                      )}
                      {canSubmit(projectData) && (
                        <CustomButton
                          label="Submit"
                          clickHandler={() => handleSubmit(projectData)}
                          btnClass={styles.submitButton}
                          showLoading={submitLoading[projectId]}
                          isDisabled={loading || submitLoading[projectId]}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WeeklyTimesheetGrid;
